// Period/cycle persistence helpers backed by the public.periods table.
import { supabase } from "@/integrations/supabase/client";
import { localDateKey } from "./dailyLogs";


export type FlowLevel = "Spotting" | "Light" | "Medium" | "Heavy";
export type FlowEntry = { date: string; level: FlowLevel };
export type PeriodRow = {
  id: string;
  user_id: string;
  start_date: string;
  end_date: string | null;
  flow_log: FlowEntry[];
  notes: string | null;
  created_at: string;
  updated_at: string;
};

// Add (or subtract, with negative n) days from a YYYY-MM-DD date key.
export function addDays(dateKey: string, n: number): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return localDateKey(dt);
}

// Insert a new period. Initial flow becomes the first flow_log entry on startDate.
// CALLER CONTRACT: if an active period exists, the UI must confirm with the user
// and pass closePriorOnDate (the date to set as the prior period's end_date) so
// the partial unique index isn't violated. Pass null to skip auto-close.
export async function savePeriodStart(
  userId: string,
  startDate: string,
  initialFlow: FlowLevel,
  notes?: string,
  closePriorOnDate?: string | null,
): Promise<PeriodRow> {
  // Atomic server-side write: close-active + insert-new run in a single RPC
  // transaction (public.start_period, SECURITY INVOKER, RLS applies).
  // Replaces the prior end-then-insert client sequence which could violate
  // the partial unique index on active periods under a race.
  void closePriorOnDate; // preserved for signature; RPC always closes any active period
  void userId;           // RPC derives caller from auth.uid()
  const { data, error } = await supabase.rpc("start_period", {
    p_start_date: startDate,
    p_initial_flow: initialFlow,
    p_notes: notes ?? undefined,
    p_close_prior_on: closePriorOnDate ?? undefined,

  });
  if (error) throw error;
  return data as unknown as PeriodRow;

}

export async function endPeriod(
  userId: string,
  periodId: string,
  endDate: string,
): Promise<void> {
  const { error } = await supabase
    .from("periods")
    .update({ end_date: endDate })
    .eq("id", periodId)
    .eq("user_id", userId);
  if (error) throw error;
}

// Append-or-replace a flow entry for a given date in flow_log (read-modify-write).
export async function updateDailyFlow(
  userId: string,
  periodId: string,
  date: string,
  flowLevel: FlowLevel,
): Promise<void> {
  const { data: row, error: readErr } = await supabase
    .from("periods")
    .select("flow_log")
    .eq("id", periodId)
    .eq("user_id", userId)
    .single();
  if (readErr) throw readErr;
  const log: FlowEntry[] = Array.isArray(row?.flow_log) ? (row!.flow_log as FlowEntry[]) : [];
  const idx = log.findIndex(e => e.date === date);
  if (idx >= 0) log[idx] = { date, level: flowLevel };
  else log.push({ date, level: flowLevel });
  log.sort((a, b) => a.date.localeCompare(b.date));
  const { error } = await supabase
    .from("periods")
    .update({ flow_log: log })
    .eq("id", periodId)
    .eq("user_id", userId);
  if (error) throw error;
}

// Periods overlapping the inclusive window.
export async function fetchPeriodsBetween(
  userId: string,
  startDate: string,
  endDate: string,
): Promise<PeriodRow[]> {
  const { data, error } = await supabase
    .from("periods")
    .select("*")
    .eq("user_id", userId)
    .lte("start_date", endDate)
    .or(`end_date.is.null,end_date.gte.${startDate}`)
    .order("start_date", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as PeriodRow[];
}

export async function fetchActivePeriod(userId: string): Promise<PeriodRow | null> {
  const { data, error } = await supabase
    .from("periods")
    .select("*")
    .eq("user_id", userId)
    .is("end_date", null)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as PeriodRow) ?? null;
}

export async function deletePeriod(userId: string, periodId: string): Promise<void> {
  const { error } = await supabase
    .from("periods")
    .delete()
    .eq("id", periodId)
    .eq("user_id", userId);
  if (error) throw error;
}

// Reshape a DB row into the legacy view shape consumed by PeriodHistory / CycleTrends.
// { start, end, days, flow, cycle, notes } where:
//   - start/end: human-readable "Mon D, YYYY"
//   - days: duration including endpoints (or flow_log size if active)
//   - flow: "dominant" flow across flow_log (mode; ties → highest intensity)
//   - cycle: filled in by the caller (needs adjacent period); left undefined here
export function rowToViewShape(row: PeriodRow): {
  id: string;
  start: string;
  end: string;
  days: number | undefined;
  flow: FlowLevel;
  cycle: number | undefined;
  notes: string;
  startKey: string;
  endKey: string | null;
  flowLog: FlowEntry[];
} {
  const startKey = row.start_date;
  const endKey = row.end_date;
  const fmt = (k: string) => {
    const [y, m, d] = k.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  let days: number | undefined;
  if (endKey) {
    const [sy, sm, sd] = startKey.split("-").map(Number);
    const [ey, em, ed] = endKey.split("-").map(Number);
    const ms = new Date(ey, em - 1, ed).getTime() - new Date(sy, sm - 1, sd).getTime();
    days = Math.round(ms / 86400000) + 1;
  } else if (Array.isArray(row.flow_log)) {
    days = row.flow_log.length || undefined;
  }
  // Dominant flow: highest intensity present (Heavy > Medium > Light > Spotting).
  const order: FlowLevel[] = ["Heavy", "Medium", "Light", "Spotting"];
  const present = new Set((row.flow_log || []).map(e => e.level));
  const flow: FlowLevel = order.find(o => present.has(o)) ?? "Medium";
  return {
    id: row.id,
    start: fmt(startKey),
    end: endKey ? fmt(endKey) : "",
    days,
    flow,
    cycle: undefined,
    notes: row.notes ?? "",
    startKey,
    endKey,
    flowLog: Array.isArray(row.flow_log) ? row.flow_log : [],
  };
}

// Compute cycle length (days between consecutive starts) and attach to view rows.
// Input: rows ordered most-recent-first.
export function attachCycleLengths<T extends { startKey: string; cycle: number | undefined }>(
  rows: T[],
): T[] {
  for (let i = 0; i < rows.length - 1; i++) {
    const [y1, m1, d1] = rows[i].startKey.split("-").map(Number);
    const [y2, m2, d2] = rows[i + 1].startKey.split("-").map(Number);
    const ms = new Date(y1, m1 - 1, d1).getTime() - new Date(y2, m2 - 1, d2).getTime();
    rows[i].cycle = Math.round(ms / 86400000);
  }
  return rows;
}
